import { useMemo } from 'react';
import {
  Avatar,
  Flex,
  Stack,
  Text,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import type { ColumnDef } from '@tanstack/react-table';
import { InfoColumnCell } from './InfoColumnCell';
import ProgressMultiColor from '../../../../progressMultiColor';
import { AttendeesCell } from './AttendeesCell';
import IgnoreIcon from '../../../../icons/IgnoreIcon';
import {
  PR_CHART_KEYS,
  PR_AGEING_CHARTS,
  PR_HEALTH_INDEX_CHARTS,
  LOC_VS_PR_CYCLE_TIME_CHARTS,
  REVIEW_RESPONSIVENESS_INDEX_CHARTS,
  GOAL_METRIC_GIT_KEYS,
  COMMIT_CHART_KEYS,
  WORK_BREAKDOWN_CHARTS,
  ISSUE_CHART_KEYS,
  GOAL_METRIC_JIRA_KEYS,
  ISSUE_AGE_CHART_KEYS,
} from '../../../../shared/utils';

interface UseMetricColumnsProps {
  defaultData: any;
  heading: string;
  currentChartMeta: any;
  onIgnoreClick: (id: string) => void;
  ignoreLoading: boolean;
  colors: any;
}

export const useMetricColumns = ({
  defaultData,
  heading,
  currentChartMeta,
  onIgnoreClick,
  ignoreLoading,
  colors,
}: UseMetricColumnsProps) => {
  const isMeetingMetric = /meeting/i.test(heading);

  const attributeKeys = useMemo(() => {
    const keys = new Set<string>();

    defaultData?.data?.forEach((item: any) => {
      item?.attributes?.forEach((att: any) => {
        if (att?.key && att.key !== 'Additions' && att.key !== 'Deletions') {
          keys.add(att.key);
        }
      });
    });

    const allKeys = Array.from(keys);

    // Always show these attribute columns first (in this order) when present
    const priorityOrder = [
      'Cycle Time',
      'PR Cycle Time',
      'Code Review Turnaround Time',
    ];

    const prioritized: string[] = [];
    priorityOrder.forEach((key) => {
      if (keys.has(key)) {
        prioritized.push(key);
      }
    });

    const remaining = allKeys.filter((key) => !priorityOrder.includes(key));

    return [...prioritized, ...remaining];
  }, [defaultData]);

  const hasReadings = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) => item.readings && item.readings.length > 0
      ),
    [defaultData]
  );

  const hasCodeChanges = useMemo(
    () =>
      !!defaultData?.data?.some((item: any) =>
        item?.attributes?.some(
          (att: any) => att?.key === 'Additions' || att?.key === 'Deletions'
        )
      ),
    [defaultData]
  );

  const hasHelped = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) => item?.helped || item?.helped === 0
      ),
    [defaultData]
  );

  const hasNewwork = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) => item?.newwork || item?.newwork === 0
      ),
    [defaultData]
  );

  const hasRefactor = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) => item?.refactor || item?.refactor === 0
      ),
    [defaultData]
  );

  const hasRework = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) => item?.rework || item?.rework === 0
      ),
    [defaultData]
  );

  const hasAuthor = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) => item?.author && item.author.trim() !== ''
      ),
    [defaultData]
  );

  const hasUpdatedAt = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) =>
          item?.updatedAt || item?.date || item?.updated_at || item?.updatedDate
      ),
    [defaultData]
  );

  const hasSprintStartDate = useMemo(
    () => !!defaultData?.data?.some((item: any) => item?.startDate),
    [defaultData]
  );

  const hasSprintEndDate = useMemo(
    () => !!defaultData?.data?.some((item: any) => item?.endDate),
    [defaultData]
  );

  const hasCommitedTasks = useMemo(
    () => !!defaultData?.data?.some((item: any) => item?.commitedTasks),
    [defaultData]
  );

  const hasCompletedTasks = useMemo(
    () => !!defaultData?.data?.some((item: any) => item?.completedTasks),
    [defaultData]
  );

  const hasUnplannedTasks = useMemo(
    () => !!defaultData?.data?.some((item: any) => item?.unplannedTasks),
    [defaultData]
  );

  const hasCommitedSP = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) => item?.commitedSP && item.commitedSP !== '0.0'
      ),
    [defaultData]
  );

  const hasCompletedSP = useMemo(
    () =>
      !!defaultData?.data?.some(
        (item: any) => item?.completedSP && item.completedSP !== '0.0'
      ),
    [defaultData]
  );

  const hasStartTime = useMemo(
    () =>
      isMeetingMetric &&
      !!defaultData?.data?.some((item: any) => item?.startTime),
    [defaultData, isMeetingMetric]
  );

  const hasEndTime = useMemo(
    () =>
      isMeetingMetric &&
      !!defaultData?.data?.some((item: any) => item?.endTime),
    [defaultData, isMeetingMetric]
  );

  const hasOrganizer = useMemo(
    () =>
      isMeetingMetric &&
      !!defaultData?.data?.some((item: any) => item?.organizer),
    [defaultData, isMeetingMetric]
  );

  const hasAttendees = useMemo(
    () =>
      isMeetingMetric &&
      !!defaultData?.data?.some(
        (item: any) => Array.isArray(item?.attendees) && item.attendees.length
      ),
    [defaultData, isMeetingMetric]
  );

  const hasCalendarOwner = useMemo(
    () =>
      isMeetingMetric &&
      !!defaultData?.data?.some((item: any) => item?.calendarOwner),
    [defaultData, isMeetingMetric]
  );

  const titleHeader = useMemo(() => {
    const chartKey = currentChartMeta?.chartKey;
    if (
      [
        ...PR_CHART_KEYS,
        ...PR_AGEING_CHARTS,
        ...PR_HEALTH_INDEX_CHARTS,
        ...LOC_VS_PR_CYCLE_TIME_CHARTS,
        ...REVIEW_RESPONSIVENESS_INDEX_CHARTS,
        ...GOAL_METRIC_GIT_KEYS,
        ...COMMIT_CHART_KEYS,
        ...WORK_BREAKDOWN_CHARTS,
      ].includes(chartKey)
    ) {
      return 'PR Title';
    }
    if (
      [
        ...ISSUE_CHART_KEYS,
        ...ISSUE_AGE_CHART_KEYS,
        ...GOAL_METRIC_JIRA_KEYS,
      ].includes(chartKey)
    ) {
      return 'Issue Title';
    }
    return 'Title';
  }, [currentChartMeta?.chartKey]);

  const columns: ColumnDef<any, any>[] = useMemo(
    () => [
      {
        header: titleHeader,
        size: 400,
        enableSorting: false,
        cell: ({ row }) => (
          <InfoColumnCell
            item={row.original}
            row={row}
            heading={heading}
            currentChartMeta={currentChartMeta}
          />
        ),
      },
      ...(hasAuthor
        ? [
            {
              header: 'Author',
              accessorKey: 'author',
              size: 260,
              enableSorting: false,
              cell: ({ row }: any) => {
                const author = row.original?.author;

                if (!author) {
                  return '-';
                }

                return (
                  <Flex align="center">
                    <Avatar size="xs" name={author} />
                    <Tooltip hasArrow label={author}>
                      <Link to={`/contributors/${author}`}>
                        <Text
                          ml={2}
                          fontSize="sm"
                          fontFamily="heading"
                          color="text.secondary"
                          fontWeight="medium"
                          textDecoration="underline"
                          noOfLines={1}
                          maxW="220px"
                        >
                          {author}
                        </Text>
                      </Link>
                    </Tooltip>
                  </Flex>
                );
              },
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasStartTime
        ? [
            {
              header: 'Start Time',
              accessorKey: 'startTime',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original?.startTime
                  ? moment(row.original.startTime).format('lll')
                  : '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasEndTime
        ? [
            {
              header: 'End Time',
              accessorKey: 'endTime',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original?.endTime
                  ? moment(row.original.endTime).format('lll')
                  : '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasOrganizer
        ? [
            {
              header: 'Organizer',
              accessorKey: 'organizer',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original?.organizer ? (
                  <Flex align="center">
                    <Avatar size="xs" name={row.original.organizer} />
                    <Text
                      ml={2}
                      fontSize="sm"
                      fontFamily="heading"
                      color="text.secondary"
                      fontWeight="medium"
                      noOfLines={1}
                    >
                      {row.original.organizer}
                    </Text>
                  </Flex>
                ) : (
                  '-'
                ),
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasAttendees
        ? [
            {
              header: 'Attendees',
              accessorKey: 'attendees',
              enableSorting: false,
              cell: ({ row }: any) => (
                <AttendeesCell attendees={row.original?.attendees} />
              ),
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasCalendarOwner
        ? [
            {
              header: 'Calendar Owner',
              accessorKey: 'calendarOwner',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original?.calendarOwner ? (
                  <Flex align="center">
                    <Avatar size="xs" name={row.original.calendarOwner} />
                    <Text
                      ml={2}
                      fontSize="sm"
                      fontFamily="heading"
                      color="text.secondary"
                      fontWeight="medium"
                      noOfLines={1}
                    >
                      {row.original.calendarOwner}
                    </Text>
                  </Flex>
                ) : (
                  '-'
                ),
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasHelped
        ? [
            {
              header: 'Helped',
              accessorKey: 'helped',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original?.helped || row.original?.helped === 0
                  ? row.original.helped
                  : '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasNewwork
        ? [
            {
              header: 'New Work',
              accessorKey: 'newwork',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original?.newwork || row.original?.newwork === 0
                  ? row.original.newwork
                  : '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasRefactor
        ? [
            {
              header: 'Refactor',
              accessorKey: 'refactor',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original?.refactor || row.original?.refactor === 0
                  ? row.original.refactor
                  : '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasRework
        ? [
            {
              header: 'Rework',
              accessorKey: 'rework',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original?.rework || row.original?.rework === 0
                  ? row.original.rework
                  : '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasUpdatedAt
        ? [
            {
              header: 'Updated At',
              accessorKey: 'updatedAt',
              enableSorting: false,
              cell: ({ row }: any) => {
                const item = row.original;
                const value =
                  item?.updatedAt ||
                  item?.date ||
                  item?.updated_at ||
                  item?.updatedDate;

                if (!value) {
                  return '-';
                }

                const formatted = moment(value).format('DD MMM YYYY, h:mm A');

                return (
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.secondary"
                    fontWeight="medium"
                    noOfLines={1}
                    whiteSpace="nowrap"
                  >
                    {formatted}
                  </Text>
                );
              },
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasSprintStartDate
        ? [
            {
              header: 'Start Date',
              accessorKey: 'startDate',
              enableSorting: false,
              size: 150,
              cell: ({ row }: any) => {
                const value = row.original?.startDate;
                if (!value) return '-';
                return (
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.secondary"
                    fontWeight="medium"
                    noOfLines={1}
                    whiteSpace="nowrap"
                  >
                    {moment(value).format('DD MMM YYYY')}
                  </Text>
                );
              },
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasSprintEndDate
        ? [
            {
              header: 'End Date',
              accessorKey: 'endDate',
              enableSorting: false,
              size: 150,
              cell: ({ row }: any) => {
                const value = row.original?.endDate;
                if (!value) return '-';
                return (
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.secondary"
                    fontWeight="medium"
                    noOfLines={1}
                    whiteSpace="nowrap"
                  >
                    {moment(value).format('DD MMM YYYY')}
                  </Text>
                );
              },
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasCommitedTasks
        ? [
            {
              header: 'Committed Tasks',
              accessorKey: 'commitedTasks',
              enableSorting: false,
              size: 140,
              cell: ({ row }: any) => row.original?.commitedTasks ?? '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasCompletedTasks
        ? [
            {
              header: 'Completed Tasks',
              accessorKey: 'completedTasks',
              enableSorting: false,
              size: 140,
              cell: ({ row }: any) => row.original?.completedTasks ?? '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasUnplannedTasks
        ? [
            {
              header: 'Unplanned Tasks',
              accessorKey: 'unplannedTasks',
              enableSorting: false,
              size: 140,
              cell: ({ row }: any) => row.original?.unplannedTasks ?? '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasCommitedSP
        ? [
            {
              header: 'Committed SP',
              accessorKey: 'commitedSP',
              enableSorting: false,
              size: 130,
              cell: ({ row }: any) => row.original?.commitedSP ?? '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasCompletedSP
        ? [
            {
              header: 'Completed SP',
              accessorKey: 'completedSP',
              enableSorting: false,
              size: 130,
              cell: ({ row }: any) => row.original?.completedSP ?? '-',
            } as ColumnDef<any, any>,
          ]
        : []),
      ...attributeKeys.map(
        (key): ColumnDef<any, any> => ({
          header: key,
          accessorKey: key,
          enableSorting: false,
          cell: ({ row }) => {
            const item = row.original;
            const att = item?.attributes?.find((a: any) => a.key === key);

            if (!att || !att.value || att.value === 'null') {
              return '-';
            }

            if (
              key === 'Merged At' ||
              key === 'Created At' ||
              key === 'Updated At' ||
              key === 'Dated At'
            ) {
              const formatted = moment(att.value).format('DD MMM YYYY, h:mm A');

              return (
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  color="text.secondary"
                  fontWeight="medium"
                  noOfLines={1}
                  whiteSpace="nowrap"
                >
                  {formatted}
                </Text>
              );
            }

            if (key === 'Reviewers') {
              const raw = att.value.replace(/[{}]/g, '');
              const reviewers = raw
                .split(',')
                .map((r: string) => r.trim())
                .filter(Boolean);

              if (reviewers.length === 0) {
                return '-';
              }

              return (
                <Stack spacing={1}>
                  {reviewers.map((reviewer: string) => (
                    <Flex key={reviewer} align="center">
                      <Avatar size="xs" name={reviewer} />
                      <Text
                        ml={2}
                        fontSize="sm"
                        fontFamily="heading"
                        color="text.secondary"
                        fontWeight="medium"
                        noOfLines={1}
                      >
                        {reviewer}
                      </Text>
                    </Flex>
                  ))}
                </Stack>
              );
            }

            if (key === 'Additions' || key === 'Deletions') {
              return '-';
            }

            return att.value;
          },
        })
      ),
      ...(hasCodeChanges
        ? [
            {
              header: 'Code Changes',
              accessorKey: 'codeChanges',
              enableSorting: false,
              cell: ({ row }: any) => {
                const item = row.original;
                const additionsAttr = item?.attributes?.find(
                  (attr: any) => attr.key === 'Additions'
                );
                const deletionsAttr = item?.attributes?.find(
                  (attr: any) => attr.key === 'Deletions'
                );
                const additions =
                  additionsAttr?.value === 'null'
                    ? 0
                    : Number(additionsAttr?.value ?? 0);
                const deletions =
                  deletionsAttr?.value === 'null'
                    ? 0
                    : Number(deletionsAttr?.value ?? 0);

                if (!additions && !deletions) {
                  return '-';
                }

                return (
                  <Flex ml={1} gap={1}>
                    <Tooltip
                      hasArrow
                      placement="bottom-start"
                      label={'Additions'}
                    >
                      <Text
                        color="success"
                        fontFamily="heading"
                        fontSize="sm"
                        fontWeight="medium"
                      >{`+${additions}`}</Text>
                    </Tooltip>
                    <Tooltip hasArrow placement="bottom-end" label="Deletions">
                      <Text
                        color="error"
                        fontFamily="heading"
                        fontSize="sm"
                        fontWeight="medium"
                      >{`-${deletions}`}</Text>
                    </Tooltip>
                  </Flex>
                );
              },
            } as ColumnDef<any, any>,
          ]
        : []),
      ...(hasReadings
        ? [
            {
              header: 'Progress',
              accessorKey: 'progress',
              enableSorting: false,
              cell: ({ row }: any) =>
                row.original.readings && row.original.readings.length > 0 ? (
                  <ProgressMultiColor unset readings={row.original.readings} />
                ) : (
                  '-'
                ),
            } as ColumnDef<any, any>,
          ]
        : []),
      ...([
        ...PR_CHART_KEYS,
        ...PR_AGEING_CHARTS,
        ...PR_HEALTH_INDEX_CHARTS,
        ...LOC_VS_PR_CYCLE_TIME_CHARTS,
        ...REVIEW_RESPONSIVENESS_INDEX_CHARTS,
        ...GOAL_METRIC_GIT_KEYS,
        ...COMMIT_CHART_KEYS,
        ...ISSUE_CHART_KEYS,
        ...GOAL_METRIC_JIRA_KEYS,
        ...WORK_BREAKDOWN_CHARTS,
      ].includes(currentChartMeta?.chartKey)
        ? ([
            {
              header: 'Action',
              accessorKey: 'action',
              enableSorting: false,
              size: 50,
              cell: ({ row }: any) => {
                const item = row.original;

                return (
                  <Tooltip
                    hasArrow
                    placement="bottom-end"
                    label={`Clicking this icon will cease tracking for this entry. Are you sure?`}
                  >
                    <Flex mx="auto" w="fit-content">
                      <IconButton
                        icon={<IgnoreIcon color={'#718096'} />}
                        size="xs"
                        onClick={() => onIgnoreClick(item.id || item.commitId)}
                        variant="solid"
                        colorScheme={colors.secondary}
                        isDisabled={ignoreLoading}
                        aria-label="Ignore entry"
                        bgColor="white"
                        p={1}
                        borderRadius="full"
                        boxShadow="sm"
                      />
                    </Flex>
                  </Tooltip>
                );
              },
            } as ColumnDef<any, any>,
          ] as ColumnDef<any, any>[])
        : []),
    ],
    [
      heading,
      currentChartMeta,
      onIgnoreClick,
      ignoreLoading,
      colors,
      hasHelped,
      hasNewwork,
      hasRefactor,
      hasRework,
      attributeKeys,
      hasCodeChanges,
      hasReadings,
      hasAuthor,
      hasUpdatedAt,
      hasStartTime,
      hasEndTime,
      hasOrganizer,
      hasAttendees,
      hasCalendarOwner,
      hasSprintStartDate,
      hasSprintEndDate,
      hasCommitedTasks,
      hasCompletedTasks,
      hasUnplannedTasks,
      hasCommitedSP,
      hasCompletedSP,
      titleHeader,
    ]
  );

  return columns;
};
